import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Plus, Trash2, Send, Download, Paperclip, File, X, Eye, User, CheckCircle, ArrowRight, ArrowLeft, AlertCircle } from 'lucide-react';
import InvoicePreview from '../shared/InvoicePreview';
import { COUNTRIES } from '../../utils/countries';
import PlanBadge from '../common/PlanBadge';
import EmailVerificationBanner from '../common/EmailVerificationBanner';
import InvoiceSuccessModal from '../common/InvoiceSuccessModal';
import { extractBusinessProfile } from '../../utils/contactNormalization';
import { checkStripeMinimum, getMinimumAmountError } from '../../utils/stripeMinimums';

const SendInvoice = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const businessProfile = useMemo(() => extractBusinessProfile(user), [user]);
  const businessWarnings = useMemo(() => {
    const issues = [];
    if (!businessProfile.companyName?.trim()) issues.push('company name');
    if (!businessProfile.address?.trim()) issues.push('street address');
    if (!businessProfile.city?.trim()) issues.push('city');
    if (!businessProfile.zipCode?.trim()) issues.push('postal code');
    if (!businessProfile.country?.trim()) issues.push('country');
    return issues;
  }, [businessProfile]);
  
  // Wizard/Stepper state
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  const [recipientType, setRecipientType] = useState('user'); // 'guest' or 'user'
  const [selectedUser, setSelectedUser] = useState(null);
  const [emailLookupLoading, setEmailLookupLoading] = useState(false);
  const [invoice, setInvoice] = useState({
    recipient: {
      companyName: '',
      firstName: '',
      lastName: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'United Kingdom'
    },
    lineItems: [
      {
        description: '',
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
        discount: 0
      }
    ],
    dueDate: '',
    currency: 'GBP',
    notes: '',
    poNumber: '',
    terms: ''
  });

  // Early payment discount state
  const [earlyPaymentDiscount, setEarlyPaymentDiscount] = useState({
    enabled: false,
    discountAmount: '',
    discountPercentage: '',
    timeframeDays: 7
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successInvoiceData, setSuccessInvoiceData] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  
  // Custom invoice number state
  const [customInvoiceNumber, setCustomInvoiceNumber] = useState('');
  const [invoiceNumberError, setInvoiceNumberError] = useState('');
  const [suggestedInvoiceNumber, setSuggestedInvoiceNumber] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  
  // Guest contact search state
  const [guestContactSearch, setGuestContactSearch] = useState('');
  const [guestContactResults, setGuestContactResults] = useState([]);
  const [guestContactSearchLoading, setGuestContactSearchLoading] = useState(false);
  const [showGuestContactDropdown, setShowGuestContactDropdown] = useState(false);
  const [recipientWarning, setRecipientWarning] = useState('');

  // Auto-populate from contactId URL parameter
  useEffect(() => {
    const contactId = searchParams.get('contactId');
    if (contactId) {
      fetchAndPopulateContact(contactId);
    }
  }, [searchParams]);

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (window.emailLookupTimeout) {
        clearTimeout(window.emailLookupTimeout);
      }
    };
  }, []);

  // Set default country to "United Kingdom" when switching to guest recipient type
  useEffect(() => {
    if (recipientType === 'guest' && (!invoice.recipient.country || invoice.recipient.country === 'GB' || invoice.recipient.country === '')) {
      setInvoice(prev => ({
        ...prev,
        recipient: {
          ...prev.recipient,
          country: 'United Kingdom'
        }
      }));
    }
  }, [recipientType]);

  useEffect(() => {
    if (recipientType === 'user') {
      setRecipientWarning('');
    }
  }, [recipientType]);

  // Expose test function to window for debugging
  useEffect(() => {
    window.testAutoPopulate = (email = 'jackielain95@gmail.com') => {
      console.log('🧪 Testing auto-population with email:', email);
      if (recipientType === 'user') {
        lookupUserByEmail(email);
      } else {
        console.log('❌ Please select "Receevit® User" recipient type first');
      }
    };
    
    return () => {
      delete window.testAutoPopulate;
    };
  }, [recipientType]); // Removed lookupUserByEmail from dependencies to prevent cycle

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length < 100;
  };

  // Fetch contact and auto-populate form
  const fetchAndPopulateContact = async (contactId) => {
    try {
      console.log('📋 Fetching contact:', contactId);
      const response = await api.get(`/contacts/${contactId}`);
      const contact = response.data.contact;
      
      console.log('✅ Contact fetched:', contact);
      
      // Check if contact is a Receevit® user or guest
      if (contact.is_receevit_user && contact.receevit_user_id) {
        // Switch to Receevit® User tab and populate
        setRecipientType('user');
        setSelectedUser({
          id: contact.receevit_user_id,
          email: contact.email,
          firstName: contact.first_name,
          lastName: contact.last_name,
          companyName: contact.company_name,
          address: contact.address_line1,
          city: contact.city,
          state: contact.state_province,
          zipCode: contact.postal_code,
          country: contact.country
        });
        setInvoice(prev => ({
          ...prev,
          recipient: {
            email: contact.email,
            firstName: contact.first_name,
            lastName: contact.last_name,
            companyName: contact.company_name,
            address: contact.address_line1 || '',
            city: contact.city,
            state: contact.state_province || '',
            zipCode: contact.postal_code,
            country: contact.country || 'United Kingdom'
          }
        }));
      } else {
        // Switch to Guest tab and populate
        setRecipientType('guest');
        setInvoice(prev => ({
          ...prev,
          recipient: {
            companyName: contact.company_name,
            firstName: contact.first_name,
            lastName: contact.last_name,
            email: contact.email,
            address: contact.address_line1 || '',
            city: contact.city,
            state: contact.state_province || '',
            zipCode: contact.postal_code,
            country: contact.country || 'United Kingdom'
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      setError('Failed to load contact details');
    }
  };

  // Search guest contacts
  const searchGuestContacts = async (query) => {
    if (!query || query.length < 2) {
      setGuestContactResults([]);
      setShowGuestContactDropdown(false);
      return;
    }
    
    try {
      setGuestContactSearchLoading(true);
      const response = await api.get(`/contacts/search?query=${encodeURIComponent(query)}&type=guest`);
      setGuestContactResults(response.data.contacts || []);
      setShowGuestContactDropdown(true);
    } catch (error) {
      console.error('Error searching guest contacts:', error);
      setGuestContactResults([]);
    } finally {
      setGuestContactSearchLoading(false);
    }
  };

  // Handle guest contact selection
  const handleSelectGuestContact = (contact) => {
    setInvoice(prev => ({
      ...prev,
      recipient: {
        companyName: contact.company_name,
        firstName: contact.first_name,
        lastName: contact.last_name,
        email: contact.email,
        address: contact.address_line1 || '',
        city: contact.city,
        state: contact.state_province || '',
        zipCode: contact.postal_code,
        country: contact.country || 'United Kingdom'
      }
    }));
    setGuestContactSearch('');
    setShowGuestContactDropdown(false);
  };

  // Email lookup function with enhanced error handling
  const lookupUserByEmail = async (email) => {
    // Validate email format and length
    if (!email || recipientType !== 'user' || !isValidEmail(email)) {
      console.log('❌ Invalid email format or recipient type:', { email, recipientType });
      setSelectedUser(null);
      return;
    }
    
    console.log('🔍 Looking up user with email:', email);
    setEmailLookupLoading(true);
    setRecipientWarning('');
    
    try {
      // First try to lookup registered user
      const response = await api.get(`/api/invoices/lookup-user/${encodeURIComponent(email)}`);
      console.log('📡 API Response:', response.data);
      
      if (response.data.success && response.data.user) {
        const user = response.data.user;
        console.log('✅ Registered user found:', user);

        const normalizedStatus = String(user.status || '').toLowerCase();
        const normalizedSubscriptionStatus = String(user.subscriptionStatus || '').toLowerCase();
        const isReceevitInactive =
          normalizedStatus === 'inactive' ||
          normalizedStatus === 'suspended' ||
          normalizedSubscriptionStatus === 'cancelled';

        if (isReceevitInactive) {
          console.log('⚠️ Registered user is inactive. Switching to guest invoice flow.');
          setSelectedUser(null);
          setRecipientType('guest');
          setRecipientWarning(
            `${user.firstName || user.companyName || 'This customer'} currently has an inactive Receevit® account. We’ve switched you to the Guest tab so you can still send the invoice via email.`
          );
          setInvoice(prev => ({
            ...prev,
            recipient: {
              companyName: user.companyName || prev.recipient.companyName || '',
              firstName: user.firstName || '',
              lastName: user.lastName || '',
              email: user.email,
              address: user.address || prev.recipient.address || '',
              city: user.city || prev.recipient.city || '',
              state: user.state || prev.recipient.state || '',
              zipCode: user.zipCode || prev.recipient.zipCode || '',
              country: user.country || prev.recipient.country || 'United Kingdom'
            }
          }));
          return;
        }
        
        // Set selected user for reference
        setSelectedUser(user);
        setRecipientWarning('');
        
        // Auto-populate form fields
        setInvoice(prev => ({
          ...prev,
          recipient: {
            ...prev.recipient,
            companyName: user.companyName || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email,
            // Optionally populate address if available
            address: user.address || prev.recipient.address,
            city: user.city || prev.recipient.city,
            state: user.state || prev.recipient.state,
            zipCode: user.zipCode || prev.recipient.zipCode,
            country: user.country || prev.recipient.country
          }
        }));
        
        // Show success message
        console.log('✅ Auto-population successful for:', user.firstName, user.lastName);
        
      } else {
        console.log('❌ Registered user lookup failed, trying contacts...');
        
        // Fallback: Try to find contact in contacts list
        try {
          const contactsResponse = await api.get('/contacts');
          console.log('📡 Contacts API Response:', contactsResponse.data);
          
          if (contactsResponse.data.success && contactsResponse.data.data?.contacts) {
            const contact = contactsResponse.data.data.contacts.find(c => 
              c.email && c.email.toLowerCase() === email.toLowerCase()
            );
            
            if (contact) {
              console.log('✅ Contact found:', contact);
              
              // Set selected user for reference (using contact data)
              setSelectedUser({
                id: contact.id,
                firstName: contact.first_name,
                lastName: contact.last_name,
                companyName: contact.company_name,
                email: contact.email,
                address: contact.address,
                city: contact.city,
                state: contact.state,
                zipCode: contact.postal_code,
                country: contact.country,
                isContact: true // Flag to indicate this is from contacts
              });
              
              // Auto-populate form fields from contact
              setInvoice(prev => ({
                ...prev,
                recipient: {
                  ...prev.recipient,
                  companyName: contact.company_name || '',
                  firstName: contact.first_name || '',
                  lastName: contact.last_name || '',
                  // Keep the email they typed
                  email: email,
                  address: contact.address || prev.recipient.address,
                  city: contact.city || prev.recipient.city,
                  state: contact.state || prev.recipient.state,
                  zipCode: contact.postal_code || prev.recipient.zipCode,
                  country: contact.country || prev.recipient.country
                }
              }));
              
              console.log('✅ Auto-population successful from contacts for:', contact.first_name, contact.last_name);
              return; // Exit early on success
            }
          }
        } catch (contactError) {
          console.log('❌ Contacts lookup failed:', contactError);
        }
        
        // If both registered user and contact lookup failed
        console.log('❌ No user or contact found for email:', email);
        setSelectedUser(null);
        
        // Clear auto-populated fields if user not found
        setInvoice(prev => ({
          ...prev,
          recipient: {
            ...prev.recipient,
            companyName: '',
            firstName: '',
            lastName: '',
            // Keep the email they typed
            email: email,
            // Keep address fields as they are
            address: prev.recipient.address,
            city: prev.recipient.city,
            state: prev.recipient.state,
            zipCode: prev.recipient.zipCode,
            country: prev.recipient.country
          }
        }));
      }
    } catch (error) {
      console.error('❌ User lookup error:', error);
      
      // Handle different types of errors
      if (error.response?.status === 404) {
        console.log('👤 User not found for email:', email);
        setSelectedUser(null);
        
        // Clear auto-populated fields
        setInvoice(prev => ({
          ...prev,
          recipient: {
            ...prev.recipient,
            companyName: '',
            firstName: '',
            lastName: '',
            email: email,
            // Keep address fields
            address: prev.recipient.address,
            city: prev.recipient.city,
            state: prev.recipient.state,
            zipCode: prev.recipient.zipCode,
            country: prev.recipient.country
          }
        }));
      } else if (error.response?.status === 401) {
        console.error('🔐 Authentication error - token may be invalid');
        // Don't clear fields, just log the error
      } else if (error.response?.status === 403) {
        console.error('🚫 Access denied - check permissions');
        // Don't clear fields, just log the error
      } else {
        console.error('🌐 Network or server error:', error.message);
        // Don't clear fields for network errors
      }
    } finally {
      setEmailLookupLoading(false);
    }
  };

  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        {
          description: '',
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
          discount: 0
        }
      ]
    }));
  };

  const removeLineItem = (index) => {
    setInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const updateLineItem = (index, field, value) => {
    setInvoice(prev => ({
      ...prev,
      lineItems: prev.lineItems.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calculateLineItemTotal = (item) => {
    const subtotal = item.quantity * item.unitPrice;
    const discount = subtotal * (item.discount / 100);
    const afterDiscount = subtotal - discount;
    const tax = afterDiscount * (item.taxRate / 100);
    return afterDiscount + tax;
  };

  const calculateInvoiceTotal = () => {
    return invoice.lineItems.reduce((total, item) => total + calculateLineItemTotal(item), 0);
  };

  // Invoice number validation
  const validateInvoiceNumber = (invoiceNumber) => {
    if (!invoiceNumber.trim()) {
      return 'Invoice number is required';
    }
    
    // Format: INV-YYYY-#### (e.g., INV-2025-0001)
    const formatRegex = /^INV-\d{4}-\d{4}$/;
    if (!formatRegex.test(invoiceNumber)) {
      return 'Format must be INV-YYYY-#### (e.g., INV-2025-0001)';
    }
    
    return null;
  };

  // Generate suggested invoice number
  const generateSuggestedInvoiceNumber = () => {
    const currentYear = new Date().getFullYear();
    const randomNum = Math.floor(Math.random() * 9000) + 1000; // 1000-9999
    return `INV-${currentYear}-${randomNum}`;
  };

  // Reset form to initial state
  const resetForm = () => {
    setInvoice({
      recipient: {
        companyName: '',
        firstName: '',
        lastName: '',
        email: '',
        address: '',
        city: '',
        state: '',
      zipCode: '',
      country: 'United Kingdom'
    },
      lineItems: [
        {
          description: '',
          quantity: 1,
          unitPrice: 0,
          taxRate: 0,
          discount: 0
        }
      ],
      dueDate: '',
      currency: 'GBP',
      notes: '',
      poNumber: '',
      terms: ''
    });
    setAttachments([]);
    
    // Reset custom invoice number fields
    const newSuggested = generateSuggestedInvoiceNumber();
    setCustomInvoiceNumber(newSuggested);
    setInvoiceNumberError('');
    setSuggestedInvoiceNumber(newSuggested);
    
    // Reset wizard to Step 1
    setCurrentStep(1);
    setCompletedSteps([]);
    
    // Reset recipient type and selection
    setRecipientType('user');
    setSelectedUser(null);
    
    // Reset early payment discount
    setEarlyPaymentDiscount({
      enabled: false,
      discountAmount: '',
      discountPercentage: '',
      timeframeDays: 7
    });
    
    // Clear errors
    setError(null);
  };

  // Initialize suggested invoice number and load saved terms on component mount
  useEffect(() => {
    const suggested = generateSuggestedInvoiceNumber();
    setSuggestedInvoiceNumber(suggested);
    setCustomInvoiceNumber(suggested);
    
    // Load saved terms and conditions from localStorage
    const savedTerms = localStorage.getItem('invoiceTerms');
    if (savedTerms) {
      setInvoice(prev => ({
        ...prev,
        terms: savedTerms
      }));
    }
  }, []);

  // Handle custom invoice number change
  const handleCustomInvoiceNumberChange = (value) => {
    setCustomInvoiceNumber(value);
    const error = validateInvoiceNumber(value);
    setInvoiceNumberError(error);
  };

  // Handle terms and conditions change and save to localStorage
  const handleTermsChange = (value) => {
    setInvoice(prev => ({
      ...prev,
      terms: value
    }));
    // Save to localStorage for future invoices
    localStorage.setItem('invoiceTerms', value);
  };

  // Wizard step validation
  const validateStep1 = () => {
    if (recipientType === 'user' && !selectedUser && !invoice.recipient.email) {
      setError('Please select a recipient or enter their email');
      return false;
    }
    if (recipientType === 'guest') {
      if (!invoice.recipient.companyName || !invoice.recipient.email) {
        setError('Company name and email are required for guest recipients');
        return false;
      }
    }
    setError(null);
    return true;
  };

  const validateStep2 = () => {
    if (!invoice.dueDate) {
      setError('Please select a due date');
      return false;
    }
    if (invoice.lineItems.length === 0) {
      setError('Please add at least one line item');
      return false;
    }
    const hasInvalidItems = invoice.lineItems.some(item => 
      !item.description.trim() || item.quantity <= 0 || item.unitPrice <= 0
    );
    if (hasInvalidItems) {
      setError('All line items must have description, quantity, and unit price');
      return false;
    }
    if (invoiceNumberError) {
      setError('Please fix the invoice number format');
      return false;
    }
    setError(null);
    return true;
  };

  // Wizard navigation
  const nextStep = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    
    setCompletedSteps(prev => [...new Set([...prev, currentStep])]);
    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToStep = (stepNumber) => {
    if (stepNumber <= currentStep || completedSteps.includes(stepNumber - 1)) {
      setCurrentStep(stepNumber);
      setError(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    // 🛡️ EMAIL VERIFICATION GUARD: Prevent unverified users from sending invoices
    // Check if emailVerified is NOT true (catches false, undefined, null)
    if (user?.emailVerified !== true) {
      setError('You must verify your email address before sending invoices. Please check your email for the verification link or click "Resend Email" in the yellow banner above.');
      setLoading(false);
      // Scroll to top to show the banner
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 🛡️ STRIPE CONNECT GUARD: Check if user has connected Stripe account (optional, backend will validate)
    try {
      const stripeStatus = await api.get('/stripe-connect/account-status');
      
      // ✅ FIX: Backend returns account object directly, not hasConnectedAccount field
      const hasAccount = !!stripeStatus.data.account;
      const isFullyConnected = stripeStatus.data.account && 
                               stripeStatus.data.account.chargesEnabled === true &&
                               stripeStatus.data.account.payoutsEnabled === true;
      
      if (stripeStatus.data.success && !hasAccount) {
        setError('You need to connect your Stripe account to receive payments. Please go to Settings → Stripe Connect to set up your account.');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      } else if (hasAccount && !isFullyConnected) {
        setError('Your Stripe account setup is incomplete. Please complete the setup in Settings → Stripe Connect to start receiving payments.');
        setLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    } catch (stripeCheckError) {
      console.log('⚠️ Could not check Stripe status (non-critical):', stripeCheckError.message);
      // Continue anyway - backend will validate and provide better error
    }

    // ✅ DEBUG: Log business profile data
    console.log('🔍 SendInvoice - Business profile validation:', {
      hasUser: !!user,
      businessProfile,
      fullUserObject: user
    });

    // ✅ VALIDATE SENDER ADDRESS (from Settings)
    const missingFields = [];
    if (!businessProfile.address || !businessProfile.address.trim()) missingFields.push('street address');
    if (!businessProfile.city || !businessProfile.city.trim()) missingFields.push('city');
    if (!businessProfile.zipCode || !businessProfile.zipCode.trim()) missingFields.push('postal code');
    if (!businessProfile.country || !businessProfile.country.trim()) missingFields.push('country');
    
    if (missingFields.length > 0) {
      console.error('❌ Address validation failed. Missing fields:', missingFields);
      console.error('❌ Business profile data:', businessProfile);
      setError(`Please complete your business address in Settings before sending invoices. Missing: ${missingFields.join(', ')}`);
      setLoading(false);
      return;
    }

    if (!businessProfile.companyName || !businessProfile.companyName.trim()) {
      setError('Please add your company name in Settings before sending invoices.');
      setLoading(false);
      return;
    }

    // ✅ VALIDATE RECIPIENT ADDRESS (mandatory for both Receevit® users and guests)
    if (!invoice.recipient.address || !invoice.recipient.address.trim()) {
      setError('Recipient street address is required');
      setLoading(false);
      return;
    }
    
    if (!invoice.recipient.city || !invoice.recipient.city.trim()) {
      setError('Recipient city is required');
      setLoading(false);
      return;
    }
    
    if (!invoice.recipient.zipCode || !invoice.recipient.zipCode.trim()) {
      setError('Recipient postal code is required');
      setLoading(false);
      return;
    }
    
    if (!invoice.recipient.country || !invoice.recipient.country.trim()) {
      setError('Recipient country is required');
      setLoading(false);
      return;
    }

    // Basic validation
    if (!invoice.recipient.email.trim()) {
      setError('Recipient email is required');
      setLoading(false);
      return;
    }

    // Prevent self-invoicing
    if (invoice.recipient.email.trim().toLowerCase() === user?.email?.toLowerCase()) {
      setError('You cannot send an invoice to yourself. Please select a different recipient.');
      setLoading(false);
      return;
    }

    // For Receevit® users, validate email format but don't require successful lookup
    if (recipientType === 'user' && !isValidEmail(invoice.recipient.email)) {
      setError('Please enter a valid email address for the Receevit® user');
      setLoading(false);
      return;
    }

    if (recipientType === 'guest' && !invoice.recipient.companyName.trim()) {
      setError('Recipient company name is required for guest invoices');
      setLoading(false);
      return;
    }

    if (invoice.lineItems.length === 0 || invoice.lineItems.some(item => !item.description.trim() || item.unitPrice <= 0)) {
      setError('Please add at least one line item with description and price');
      setLoading(false);
      return;
    }

    if (!invoice.dueDate) {
      setError('Due date is required');
      setLoading(false);
      return;
    }
    
    // Validate due date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
    const selectedDueDate = new Date(invoice.dueDate);
    selectedDueDate.setHours(0, 0, 0, 0);
    
    if (selectedDueDate < today) {
      setError('Due date cannot be in the past. Please select today or a future date.');
      setLoading(false);
      return;
    }

    // Custom invoice number validation (now mandatory)
    if (!customInvoiceNumber.trim()) {
      setError('Invoice number is required');
      setLoading(false);
      return;
    }
    
    const invoiceNumberValidationError = validateInvoiceNumber(customInvoiceNumber);
    if (invoiceNumberValidationError) {
      setError(invoiceNumberValidationError);
      setLoading(false);
      return;
    }

    // Validate Stripe minimum amount before creating invoice
    const invoiceTotal = calculateInvoiceTotal();
    const minCheck = checkStripeMinimum(invoiceTotal, invoice.currency);
    if (minCheck.isBelowMinimum) {
      setError(getMinimumAmountError(invoiceTotal, invoice.currency));
      setLoading(false);
      return;
    }

    if (recipientType === 'user') {
      if (!selectedUser || selectedUser.isContact) {
        setError('This email is not linked to an active Receevit® user. Switch to "Guest" and complete the recipient details.');
        setLoading(false);
        return;
      }
      const selectedStatus = String(selectedUser.status || '').toLowerCase();
      const selectedSubscriptionStatus = String(selectedUser.subscriptionStatus || '').toLowerCase();
      if (
        selectedStatus === 'inactive' ||
        selectedStatus === 'suspended' ||
        selectedSubscriptionStatus === 'cancelled'
      ) {
        setError('This Receevit® user is inactive. Please send the invoice via the Guest recipient tab instead.');
        setLoading(false);
        return;
      }
    }

    try {
      // Prepare payload based on recipient type
      let payload;
      
      if (recipientType === 'user' && selectedUser) {
        // Send to registered user
        payload = {
          receiverId: selectedUser.id,
          items: invoice.lineItems,
          dueDate: invoice.dueDate,
          currency: invoice.currency,
          notes: invoice.notes,
          poNumber: invoice.poNumber,
          terms: invoice.terms,
          attachments: attachments.map(att => ({
            name: att.name,
            size: att.size,
            type: att.type,
            s3Key: att.s3Key
          })),
          // Include early payment discount if enabled
          earlyPaymentDiscount: earlyPaymentDiscount.enabled ? {
            discountAmount: earlyPaymentDiscount.discountAmount ? parseFloat(earlyPaymentDiscount.discountAmount) : null,
            discountPercentage: earlyPaymentDiscount.discountPercentage ? parseFloat(earlyPaymentDiscount.discountPercentage) : null,
            timeframeDays: earlyPaymentDiscount.timeframeDays
          } : null,
          // Include custom invoice number (now mandatory)
          customInvoiceNumber: customInvoiceNumber
        };
      } else {
        // Send to guest
        payload = {
          recipient: invoice.recipient,
          lineItems: invoice.lineItems,
          dueDate: invoice.dueDate,
          currency: invoice.currency,
          notes: invoice.notes,
          poNumber: invoice.poNumber,
          terms: invoice.terms,
          attachments: attachments.map(att => ({
            name: att.name,
            size: att.size,
            type: att.type,
            s3Key: att.s3Key
          })),
          // Include custom invoice number (now mandatory)
          customInvoiceNumber: customInvoiceNumber
        };
      }

      // Call real backend API using the API utility
      const response = await api.post('/invoices', payload);

      if (response.data.success) {
        // Store invoice data for modal
        setSuccessInvoiceData(response.data.data || {
          invoiceNumber: customInvoiceNumber,
          total: calculateInvoiceTotal(),
          currency: invoice.currency,
          dueDate: invoice.dueDate
        });
        
        // Show success modal
        setShowSuccessModal(true);
      } else {
        setError(response.data.message || 'Failed to send invoice. Please try again.');
      }
    } catch (error) {
      console.error('Error sending invoice:', error);
      console.error('Error response data:', error.response?.data);
      
      // Handle specific error cases with user-friendly messages
      const errorData = error.response?.data || {};
      
      // Check for AMOUNT_BELOW_MINIMUM error code
      if (errorData.code === 'AMOUNT_BELOW_MINIMUM' || errorData.error === 'Invoice amount below minimum') {
        // Show detailed minimum amount error message
        const errorMessage = errorData.details || errorData.error || 'Invoice amount is below the minimum required amount.';
        setError(errorMessage);
      } else if (errorData.error) {
        // Show backend error message with details if available
        let errorMsg = errorData.error;
        if (errorData.details) {
          errorMsg += '\n\n' + errorData.details;
        }
        setError(errorMsg);
      } else if (errorData.message) {
        // Show backend message
        setError(errorData.message);
      } else if (error.message) {
        // Show error message
        setError(error.message);
      } else {
        // Generic fallback
        setError('Failed to send invoice. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };



  const handleFileUpload = async (files) => {
    const filesArray = Array.from(files);
    
    for (const file of filesArray) {
      try {
        // Validate file size (10MB max)
        if (file.size > 10 * 1024 * 1024) {
          alert(`${file.name} is too large. Maximum file size is 10MB.`);
          continue;
        }
        
        // Request upload URL from backend
        const uploadResponse = await api.post('/files/upload-url', {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          category: 'invoice'
        });
        
        if (!uploadResponse.data.success) {
          alert(`Failed to prepare upload for ${file.name}`);
          continue;
        }
        
        const { uploadUrl, fileKey } = uploadResponse.data;
        
        // Upload file directly to S3
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type
          }
        });
        
        // Add to attachments with S3 key
        const newAttachment = {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          s3Key: fileKey,
          uploadedAt: new Date().toISOString()
        };
        
        setAttachments(prev => [...prev, newAttachment]);
        
      } catch (error) {
        console.error('Error uploading file:', error);
        alert(`Failed to upload ${file.name}. Please try again.`);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const removeAttachment = (id) => {
    setAttachments(prev => prev.filter(attachment => attachment.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handlePreview = () => {
    setPreviewOpen(true);
  };


  const closePreview = () => {
    setPreviewOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between" data-tour="invoice-creation">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Send Invoice</h1>
          <p className="text-gray-600">Create and send a professional invoice to your client</p>
        </div>
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={handlePreview}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50"
          >
            <Send className="w-4 h-4 mr-2" />
            {loading ? 'Sending...' : 'Send Invoice'}
          </button>
        </div>
      </div>

      {/* Enhanced Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 flex items-start space-x-3 shadow-sm">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800">Error</p>
            <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800 transition-colors"
            aria-label="Close error"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {businessWarnings.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-900 px-4 py-3 rounded-lg">
          <p className="font-semibold">Complete your business profile</p>
          <p className="text-sm mt-1">
            Add your {businessWarnings.join(', ')} in Settings → Account Info before sending an invoice.
          </p>
        </div>
      )}

      {/* Progress Stepper */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          {[
            { num: 1, title: 'Recipient', desc: 'Who receives this invoice?', icon: User },
            { num: 2, title: 'Invoice Details', desc: 'What are you billing for?', icon: File },
            { num: 3, title: 'Review & Send', desc: 'Final check', icon: Send }
          ].map((step, idx, arr) => (
            <React.Fragment key={step.num}>
              <div className="flex flex-col items-center flex-1">
                <button
                  type="button"
                  onClick={() => goToStep(step.num)}
                  disabled={step.num > currentStep && !completedSteps.includes(step.num - 1)}
                  className={`w-14 h-14 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                    currentStep === step.num
                      ? 'bg-[#1F11ED] text-white shadow-lg scale-110 ring-4 ring-blue-100'
                      : completedSteps.includes(step.num)
                      ? 'bg-green-500 text-white shadow-md cursor-pointer hover:bg-green-600'
                      : step.num < currentStep
                      ? 'bg-gray-300 text-gray-600 cursor-pointer hover:bg-gray-400'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  {completedSteps.includes(step.num) ? (
                    <CheckCircle className="w-7 h-7" />
                  ) : (
                    <span className="text-lg">{step.num}</span>
                  )}
                </button>
                <div className="mt-3 text-center">
                  <p className={`text-sm font-semibold ${
                    currentStep === step.num ? 'text-[#1F11ED]' : 'text-gray-700'
                  }`}>
                    {step.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{step.desc}</p>
                </div>
              </div>
              
              {idx < arr.length - 1 && (
                <div className="flex-1 h-1 mx-6 mt-[-50px] relative">
                  <div className="absolute inset-0 bg-gray-200 rounded-full"></div>
                  <div 
                    className={`absolute inset-0 rounded-full transition-all duration-500 ${
                      completedSteps.includes(step.num) ? 'bg-green-500' : 'bg-gray-200'
                    }`}
                    style={{ 
                      width: completedSteps.includes(step.num) ? '100%' : '0%',
                      transition: 'width 0.5s ease-in-out'
                    }}
                  ></div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); }}>
      
      {/* STEP 1: RECIPIENT */}
      {currentStep === 1 && (
        <div className="space-y-6">
      {/* Recipient Type Toggle */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-base font-medium text-gray-900 mb-4">Recipient Type</h3>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="recipientType"
              value="guest"
              checked={recipientType === 'guest'}
              onChange={(e) => {
                setRecipientType(e.target.value);
                setSelectedUser(null);
                // Clear recipient fields when switching to guest, default country to United Kingdom
                setInvoice(prev => ({
                  ...prev,
                  recipient: {
                    companyName: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    country: 'United Kingdom'
                  }
                }));
              }}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Guest (External)</span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="recipientType"
              value="user"
              checked={recipientType === 'user'}
              onChange={(e) => {
                setRecipientType(e.target.value);
                setSelectedUser(null);
                // Clear recipient fields when switching to user
                setInvoice(prev => ({
                  ...prev,
                  recipient: {
                    companyName: '',
                    firstName: '',
                    lastName: '',
                    email: '',
                    address: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    country: prev.recipient.country || 'United Kingdom'
                  }
                }));
              }}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Receevit® User</span>
          </label>
        </div>
        {recipientType === 'user' && (
          <p className="text-sm text-gray-500 mt-2">
            Enter the user's email address and their details will be automatically populated.
          </p>
        )}
      </div>

      {recipientWarning && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <span className="mt-1 inline-flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600">
            <AlertCircle className="w-4 h-4" />
          </span>
          <div className="text-sm text-amber-800">
            <p className="font-medium">Send this invoice as a guest recipient</p>
            <p className="mt-1">{recipientWarning}</p>
          </div>
        </div>
      )}

        {/* Guest Contact Search (only for guest type) */}
        {recipientType === 'guest' && (
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Quick Fill from Contacts
              <span className="text-xs text-gray-500 ml-2">(Optional - or fill manually below)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={guestContactSearch}
                onChange={(e) => {
                  setGuestContactSearch(e.target.value);
                  searchGuestContacts(e.target.value);
                }}
                onFocus={() => {
                  if (guestContactResults.length > 0) setShowGuestContactDropdown(true);
                }}
                placeholder="Search your contacts..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              
              {guestContactSearchLoading && (
                <div className="absolute right-3 top-3">
                  <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              )}
              
              {/* Contact search results dropdown */}
              {showGuestContactDropdown && guestContactResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {guestContactResults.map((contact) => (
                    <button
                      key={contact.id}
                      type="button"
                      onClick={() => handleSelectGuestContact(contact)}
                      className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <p className="font-medium text-gray-900">{contact.company_name}</p>
                      <p className="text-sm text-gray-600">{contact.first_name} {contact.last_name}</p>
                      <p className="text-xs text-gray-500">{contact.email}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recipient Information */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">Recipient Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <span>Company Name *</span>
                  {selectedUser && recipientType === 'user' && (
                    <>
                      <PlanBadge plan={selectedUser.subscriptionPlan} />
                      <span className="text-xs text-green-600">(Auto-filled)</span>
                    </>
                  )}
                </div>
              </label>
              <input
                type="text"
                required
                value={invoice.recipient.companyName}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, companyName: e.target.value }
                }))}
                readOnly={selectedUser && recipientType === 'user'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  selectedUser && recipientType === 'user' ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="Company Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Email *
                {recipientType === 'user' && (
                  <span className="text-xs text-blue-600 ml-1">(Auto-populates user details)</span>
                )}
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  value={invoice.recipient.email}
                  onChange={(e) => {
                    const emailValue = e.target.value.trim();
                    console.log('📧 Email input changed:', emailValue);
                    console.log('📧 Email length:', emailValue.length);
                    console.log('📧 Recipient type:', recipientType);
                    
                    setInvoice(prev => ({
                      ...prev,
                      recipient: { ...prev.recipient, email: emailValue }
                    }));
                    
                    // Clear selected user when email changes
                    if (selectedUser && selectedUser.email !== emailValue) {
                      setSelectedUser(null);
                    }
                    
                    // Trigger lookup after a short delay with debouncing
                    if (recipientType === 'user' && emailValue && isValidEmail(emailValue)) {
                      // Clear any existing timeout
                      if (window.emailLookupTimeout) {
                        clearTimeout(window.emailLookupTimeout);
                      }
                      
                      // Set new timeout
                      window.emailLookupTimeout = setTimeout(() => {
                        lookupUserByEmail(emailValue);
                      }, 800); // Increased delay for better debouncing
                    } else if (!emailValue) {
                      // Clear fields when email is empty
                      setSelectedUser(null);
                      setInvoice(prev => ({
                        ...prev,
                        recipient: {
                          ...prev.recipient,
                          companyName: '',
                          firstName: '',
                          lastName: '',
                          email: '',
                          // Keep address fields
                          address: prev.recipient.address,
                          city: prev.recipient.city,
                          state: prev.recipient.state,
                          zipCode: prev.recipient.zipCode,
                          country: prev.recipient.country
                        }
                      }));
                    }
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    selectedUser && recipientType === 'user' 
                      ? 'border-green-300 bg-green-50' 
                      : 'border-gray-300'
                  }`}
                  placeholder="email@company.com"
                />
                
                {/* Loading indicator */}
                {emailLookupLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
                
                {/* Success indicator */}
                {selectedUser && recipientType === 'user' && !emailLookupLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="text-green-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Manual lookup button for Receevit® users */}
              {recipientType === 'user' && invoice.recipient.email && !selectedUser && !emailLookupLoading && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => {
                      console.log('🔍 Manual lookup triggered for:', invoice.recipient.email);
                      lookupUserByEmail(invoice.recipient.email);
                    }}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                  >
                    🔍 Lookup User Details
                  </button>
                </div>
              )}
              
              {/* Fallback message when lookup fails */}
              {recipientType === 'user' && invoice.recipient.email && !selectedUser && !emailLookupLoading && (
                <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>Auto-populate failed, but you can still send the invoice by filling in the details manually below.</span>
                  </div>
                </div>
              )}
              
              {/* User feedback messages */}
              {selectedUser && recipientType === 'user' && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">User found and details auto-populated:</span>
                  </div>
                  <div className="mt-1 ml-6">
                    <div><strong>Name:</strong> {selectedUser.firstName} {selectedUser.lastName}</div>
                    <div><strong>Company:</strong> {selectedUser.companyName}</div>
                    <div><strong>Email:</strong> {selectedUser.email}</div>
                  </div>
                </div>
              )}
              
              {/* Instructions for user */}
              {recipientType === 'user' && !selectedUser && !emailLookupLoading && (
                <div className="mt-2 text-xs text-gray-500">
                  💡 Enter a valid email address to automatically populate user details
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name
                {selectedUser && recipientType === 'user' && (
                  <span className="text-xs text-green-600 ml-1">(Auto-filled)</span>
                )}
              </label>
              <input
                type="text"
                value={invoice.recipient.firstName}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, firstName: e.target.value }
                }))}
                readOnly={selectedUser && recipientType === 'user'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  selectedUser && recipientType === 'user' ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="First Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name
                {selectedUser && recipientType === 'user' && (
                  <span className="text-xs text-green-600 ml-1">(Auto-filled)</span>
                )}
              </label>
              <input
                type="text"
                value={invoice.recipient.lastName}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, lastName: e.target.value }
                }))}
                readOnly={selectedUser && recipientType === 'user'}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  selectedUser && recipientType === 'user' ? 'bg-gray-50 cursor-not-allowed' : ''
                }`}
                placeholder="Last Name"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address *
              </label>
              <input
                type="text"
                required
                value={invoice.recipient.address}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, address: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="123 Business Street"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City *
              </label>
              <input
                type="text"
                required
                value={invoice.recipient.city}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, city: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="London"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State/County
              </label>
              <input
                type="text"
                value={invoice.recipient.state}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, state: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="England (optional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ZIP/Postal Code *
              </label>
              <input
                type="text"
                required
                value={invoice.recipient.zipCode}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, zipCode: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="SW1A 1AA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <select
                required
                value={invoice.recipient.country}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  recipient: { ...prev.recipient, country: e.target.value }
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {COUNTRIES.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Step 1 Navigation */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={nextStep}
            className="inline-flex items-center px-8 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold shadow-lg"
          >
            Continue to Invoice Details
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
        </div>
      )}

      {/* STEP 2: INVOICE DETAILS */}
      {currentStep === 2 && (
        <div className="space-y-6">
        {/* Invoice Details */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">Invoice Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Due Date *
              </label>
              <input
                type="date"
                required
                value={invoice.dueDate || ''}
                min={new Date().toISOString().split('T')[0]} // Set minimum date to today
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  
                  // Always update the value to allow user to type freely
                  // Validation will happen on blur
                  setInvoice(prev => ({
                    ...prev,
                    dueDate: selectedDate
                  }));
                  
                  // Clear any previous errors when user starts typing
                  if (error && error.includes('Due date')) {
                    setError(null);
                  }
                }}
                onBlur={(e) => {
                  // Validate only when user finishes editing (on blur)
                  const selectedDate = e.target.value;
                  if (selectedDate) {
                    const today = new Date().toISOString().split('T')[0];
                    
                    if (selectedDate < today) {
                      setError('Due date cannot be in the past. Please select today or a future date.');
                    }
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {invoice.dueDate && new Date(invoice.dueDate) < new Date(new Date().setHours(0, 0, 0, 0)) && (
                <p className="mt-1 text-xs text-red-600">⚠️ Due date cannot be in the past</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Invoice Number *
              </label>
              <input
                type="text"
                value={customInvoiceNumber}
                onChange={(e) => handleCustomInvoiceNumberChange(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  invoiceNumberError ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="INV-2025-0001"
                required
              />
              {invoiceNumberError && (
                <p className="text-sm text-red-600 mt-1">{invoiceNumberError}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Format: INV-YYYY-#### (e.g., INV-2025-0001)
              </p>
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-lg border shadow-sm p-6" data-tour="invoice-templates">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-gray-900">Line Items</h3>
            <button
              type="button"
              onClick={addLineItem}
              className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </button>
          </div>
          
          <div className="space-y-4">
            {invoice.lineItems.map((item, index) => (
              <div key={index} className="grid grid-cols-12 gap-4 items-end border border-gray-200 rounded-lg p-4">
                <div className="col-span-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <input
                    type="text"
                    required
                    value={item.description}
                    onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Item description"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Qty
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(index, 'quantity', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unit Price
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.unitPrice}
                    onChange={(e) => updateLineItem(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax %
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={item.taxRate}
                    onChange={(e) => updateLineItem(index, 'taxRate', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="col-span-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount %
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={item.discount}
                    onChange={(e) => updateLineItem(index, 'discount', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900 font-medium">
                    {invoice.currency === 'GBP' ? '£' : '$'}{calculateLineItemTotal(item).toFixed(2)}
                  </div>
                </div>
                <div className="col-span-1">
                  {invoice.lineItems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeLineItem(index)}
                      className="w-full px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Early Payment Discount - Only for registered users */}
        {recipientType === 'user' && (
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base font-medium text-gray-900">Early Payment Discount</h3>
                <p className="text-sm text-gray-600">Incentivise early payment with a discount on their next invoice</p>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={earlyPaymentDiscount.enabled}
                  onChange={(e) => setEarlyPaymentDiscount(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">Enable early payment discount</span>
              </label>
            </div>

            {earlyPaymentDiscount.enabled && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Amount (£)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={earlyPaymentDiscount.discountAmount}
                      onChange={(e) => setEarlyPaymentDiscount(prev => ({ 
                        ...prev, 
                        discountAmount: e.target.value,
                        discountPercentage: '' // Clear percentage when amount is set
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Discount Percentage (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={earlyPaymentDiscount.discountPercentage}
                      onChange={(e) => setEarlyPaymentDiscount(prev => ({ 
                        ...prev, 
                        discountPercentage: e.target.value,
                        discountAmount: '' // Clear amount when percentage is set
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Timeframe (Days)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={earlyPaymentDiscount.timeframeDays}
                      onChange={(e) => setEarlyPaymentDiscount(prev => ({ 
                        ...prev, 
                        timeframeDays: parseInt(e.target.value) || 1 
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter days (e.g., 7, 14, 30)"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <span className="text-blue-600 text-lg">⚡</span>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-blue-800">
                        <strong>How it works:</strong> If this invoice is paid within {earlyPaymentDiscount.timeframeDays} days, 
                        the receiver will automatically receive a discount of{' '}
                        {earlyPaymentDiscount.discountAmount ? `£${earlyPaymentDiscount.discountAmount}` : 
                         earlyPaymentDiscount.discountPercentage ? `${earlyPaymentDiscount.discountPercentage}%` : '£0.00'}
                        {' '}on their next invoice from you.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}


        {/* Additional Information */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">Additional Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                rows="4"
                value={invoice.notes}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  notes: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Additional notes for the client..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Purchase Order Number
              </label>
              <input
                type="text"
                value={invoice.poNumber}
                onChange={(e) => setInvoice(prev => ({
                  ...prev,
                  poNumber: e.target.value
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter PO number (optional)"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms & Conditions
              </label>
              <textarea
                rows="4"
                value={invoice.terms}
                onChange={(e) => handleTermsChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Payment terms and conditions..."
              />
            </div>
          </div>
        </div>

        {/* Attachments */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4">Attachments</h3>
          
          {/* File Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors duration-200 ${
              dragActive 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Paperclip className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-base font-medium text-gray-900 mb-2">
              {dragActive ? 'Drop files here' : 'Upload files'}
            </p>
            <p className="text-gray-600 mb-4">
              Drag and drop files here, or click to browse
            </p>
            <input
              type="file"
              multiple
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              id="file-upload"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
            />
            <label
              htmlFor="file-upload"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 cursor-pointer"
            >
              <Plus className="w-4 h-4 mr-2" />
              Choose Files
            </label>
            <p className="text-sm text-gray-500 mt-2">
              Supported formats: PDF, DOC, XLS, Images, TXT (Max 10MB per file)
            </p>
          </div>

          {/* Attachments List */}
          {attachments.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Uploaded Files ({attachments.length})</h4>
              <div className="space-y-2">
                {attachments.map((attachment) => (
                  <div
                    key={attachment.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-center space-x-3">
                      <File className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{attachment.name}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(attachment.size)} • {attachment.type}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(attachment.id)}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors duration-200"
                      title="Remove attachment"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Step 2 Navigation */}
        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={prevStep}
            className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Recipient
          </button>
          <button
            type="button"
            onClick={nextStep}
            className="inline-flex items-center px-8 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-semibold shadow-lg"
          >
            Review & Send
            <ArrowRight className="w-5 h-5 ml-2" />
          </button>
        </div>
        </div>
      )}

      {/* STEP 3: REVIEW & SEND */}
      {currentStep === 3 && (
        <div className="space-y-6">
        {/* Invoice Summary */}
        <div className="bg-white rounded-lg border shadow-sm p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Summary</h3>
          
          {/* Line Items Breakdown Table */}
          <div className="mb-6 overflow-x-auto">
            <table className="w-full border-collapse border border-gray-200">
              <thead>
                <tr className="bg-gray-50">
                  <th className="border border-gray-200 px-4 py-2 text-left text-sm font-medium text-gray-700">Description</th>
                  <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700">Qty</th>
                  <th className="border border-gray-200 px-4 py-2 text-right text-sm font-medium text-gray-700">Unit Price</th>
                  <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700">Tax %</th>
                  <th className="border border-gray-200 px-4 py-2 text-center text-sm font-medium text-gray-700">Discount %</th>
                  <th className="border border-gray-200 px-4 py-2 text-right text-sm font-medium text-gray-700">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2 text-sm text-gray-900">{item.description || '-'}</td>
                    <td className="border border-gray-200 px-4 py-2 text-center text-sm text-gray-900">{item.quantity}</td>
                    <td className="border border-gray-200 px-4 py-2 text-right text-sm text-gray-900">
                      {invoice.currency === 'GBP' ? '£' : '$'}{item.unitPrice.toFixed(2)}
                    </td>
                    <td className="border border-gray-200 px-4 py-2 text-center text-sm text-gray-900">{item.taxRate}%</td>
                    <td className="border border-gray-200 px-4 py-2 text-center text-sm text-gray-900">{item.discount}%</td>
                    <td className="border border-gray-200 px-4 py-2 text-right text-sm font-medium text-gray-900">
                      {invoice.currency === 'GBP' ? '£' : '$'}{calculateLineItemTotal(item).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal:</span>
                <span className="font-medium">
                  {invoice.currency === 'GBP' ? '£' : '$'}{invoice.lineItems.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax:</span>
                <span className="font-medium">
                  {invoice.currency === 'GBP' ? '£' : '$'}{invoice.lineItems.reduce((sum, item) => {
                    const subtotal = item.quantity * item.unitPrice;
                    const discount = subtotal * (item.discount / 100);
                    const afterDiscount = subtotal - discount;
                    return sum + (afterDiscount * (item.taxRate / 100));
                  }, 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discounts:</span>
                <span className="font-medium text-red-600">
                  -{invoice.currency === 'GBP' ? '£' : '$'}{invoice.lineItems.reduce((sum, item) => {
                    const subtotal = item.quantity * item.unitPrice;
                    return sum + (subtotal * (item.discount / 100));
                  }, 0).toFixed(2)}
                </span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span>{invoice.currency === 'GBP' ? '£' : '$'}{calculateInvoiceTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3 Navigation */}
        <div className="flex justify-between items-center pt-4">
          <button
            type="button"
            onClick={prevStep}
            className="inline-flex items-center px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Invoice Details
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || invoiceNumberError}
            className="inline-flex items-center px-10 py-4 bg-[#1F11ED] text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-bold shadow-xl text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-6 h-6 mr-2" />
            {loading ? 'Sending Invoice...' : 'Send Invoice Now'}
          </button>
        </div>
        </div>
      )}
      
      </form>

      {/* Preview Modal - Using Shared Modern Invoice Preview */}
      <InvoicePreview
        invoice={{
          ...invoice,
          invoiceNumber: customInvoiceNumber || '[Invoice number will be set]',
          sender: {
            firstName: user?.firstName,
            lastName: user?.lastName,
            companyName: user?.companyName,
            email: user?.email,
            address: user?.address,
            city: user?.city,
            state: user?.state,
            zipCode: user?.zipCode,
            country: user?.country
          },
          receiver: invoice.recipient,
          createdAt: new Date().toISOString(),
          status: 'preview'
        }}
        isOpen={previewOpen}
        onClose={closePreview}
        showDownloadButtons={false}
        title="Invoice Preview (Draft)"
      />

      {/* Success Modal */}
      <InvoiceSuccessModal
        isOpen={showSuccessModal}
        onClose={() => {
          setShowSuccessModal(false);
          resetForm();
        }}
        invoiceData={successInvoiceData}
        recipientName={
          recipientType === 'user' && selectedUser
            ? `${selectedUser.firstName} ${selectedUser.lastName}`
            : invoice.recipient.companyName || `${invoice.recipient.firstName} ${invoice.recipient.lastName}`
        }
        recipientEmail={invoice.recipient.email}
        onSendAnother={() => {
          setShowSuccessModal(false);
          resetForm();
        }}
      />
    </div>
  );
};

export default SendInvoice;
